import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { z } from "zod";

import { DatePickerField } from "@/components/shared/DatePickerField";
import { PageHeader } from "@/components/shared/PageHeader";
import { SupplierPicker } from "@/components/features/suppliers/SupplierPicker";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { expenseKeys } from "@/lib/queryKeys";
import { createExpense } from "@/services/expensesService";
import type { Supplier } from "@/services/supplierService";

const CATEGORIES = [
  "Comida",
  "Transporte",
  "Software",
  "Oficina",
  "Servicios",
  "Otros",
] as const;

const schema = z.object({
  occurredAt: z.string().min(1),
  vendor: z.string().max(255).optional(),
  vendorRut: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^\d{12}$/.test(v.replace(/\D/g, "")),
      "RUT debe tener 12 dígitos",
    ),
  currency: z.enum(["UYU", "USD"]),
  total: z
    .string()
    .min(1, "Monto requerido")
    .refine(
      (v) => /^\d+([.,]\d{1,2})?$/.test(v.trim()),
      "Monto inválido (ej: 1250.50)",
    ),
  iva: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^\d+([.,]\d{1,2})?$/.test(v.trim()),
      "IVA inválido",
    ),
  category: z.string().max(64).optional(),
  notes: z.string().max(2000).optional(),
});

type FormValues = z.infer<typeof schema>;

function parseMoney(s: string): number {
  const t = s.trim();
  if (t.includes(",")) return parseFloat(t.replace(/\./g, "").replace(",", "."));
  return parseFloat(t);
}

export function NewExpensePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const todayIso = new Date().toISOString().slice(0, 10);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      occurredAt: todayIso,
      currency: "UYU",
      total: "",
      category: "Comida",
    },
  });

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  const handleSupplierChange = (supplier: Supplier | null): void => {
    setSelectedSupplier(supplier);
    if (!supplier) return;
    // Pre-fill vendor + RUT + category + currency from the supplier.
    // Don't overwrite the user's own edits if they already typed something
    // (only pre-fill blanks) — keeps the typeahead non-destructive.
    if (supplier.legalName && !control._formValues.vendor) {
      setValue("vendor", supplier.displayName?.trim() || supplier.legalName);
    }
    if (supplier.rut && !control._formValues.vendorRut) {
      setValue("vendorRut", supplier.rut);
    }
    if (supplier.defaultCategory && !control._formValues.category) {
      setValue("category", supplier.defaultCategory);
    }
    if (
      (supplier.defaultCurrency === "UYU" || supplier.defaultCurrency === "USD") &&
      control._formValues.currency === "UYU"
    ) {
      setValue("currency", supplier.defaultCurrency);
    }
  };

  const createMutation = useMutation({
    mutationFn: createExpense,
    onSuccess: () => {
      toast.success(t("expenses.new.created", "Gasto guardado"));
      void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
      navigate("/expenses");
    },
    onError: (err) => {
      const msg =
        err instanceof Error ? err.message : "No pudimos guardar el gasto";
      toast.error(
        t("expenses.new.error", "No pudimos guardar el gasto"),
        { description: msg } as never,
      );
    },
  });

  const onSubmit = (values: FormValues): void => {
    createMutation.mutate({
      occurredAt: values.occurredAt,
      vendor: values.vendor?.trim() || undefined,
      vendorRut: values.vendorRut?.replace(/\D/g, "") || undefined,
      currency: values.currency,
      iva: values.iva ? parseMoney(values.iva) : undefined,
      total: parseMoney(values.total),
      category: values.category?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
    });
  };

  const onInvalid = (): void => {
    // Surface the first validation error as a toast — paired with the
    // inline messages already painted by react-hook-form below each field.
    const firstError = Object.values(errors).find(
      (e) => e && "message" in e,
    );
    const description =
      firstError && "message" in firstError && firstError.message
        ? String(firstError.message)
        : undefined;
    toast.error(
      t("expenses.new.invalid", "Revisá los campos marcados"),
      description ? ({ description } as never) : undefined,
    );
  };

  return (
    <div className="fx-content space-y-4">
      <PageHeader
        title={t("expenses.new.title", "Nuevo gasto")}
        description={t(
          "expenses.new.description",
          "Cargá un ticket o factura para tener el comprobante a mano y reconciliar tu IVA compras.",
        )}
        onBack={() => navigate("/expenses")}
      />

      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className="space-y-4"
      >
        <Card>
          <CardHeader>
            <CardTitle>
              {t("expenses.new.comprobante", "Comprobante")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Controller
                name="occurredAt"
                control={control}
                render={({ field }) => (
                  <DatePickerField
                    id="occurredAt"
                    label={`${t("expenses.new.date", "Fecha")} *`}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={errors.occurredAt?.message}
                    endMonth={new Date()}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t("expenses.new.supplier", "Proveedor (opcional)")}
              </Label>
              <SupplierPicker
                value={selectedSupplier}
                onChange={handleSupplierChange}
                disabled={createMutation.isPending}
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  "expenses.new.supplierHint",
                  "Si elegís un proveedor existente autocompletamos el nombre, RUT y categoría.",
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">
                {t("expenses.new.vendor", "Comercio")}
              </Label>
              <Input
                id="vendor"
                {...register("vendor")}
                placeholder={t(
                  "expenses.new.vendorPlaceholder",
                  "Ej: Tienda Inglesa, Uber",
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendorRut">
                {t("expenses.new.vendorRut", "RUT del comercio")}
              </Label>
              <Input
                id="vendorRut"
                inputMode="numeric"
                maxLength={12}
                {...register("vendorRut")}
                placeholder="123456780019"
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  "expenses.new.vendorRutHelp",
                  "Opcional — 12 dígitos. Te ayuda a reconciliar con el CFE recibido más adelante.",
                )}
              </p>
              {errors.vendorRut && (
                <p className="text-sm text-destructive">
                  {errors.vendorRut.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("expenses.new.montos", "Montos")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>{t("expenses.new.currency", "Moneda")}</Label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UYU">UYU</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="total">
                  {t("expenses.new.total", "Total")} *
                </Label>
                <Input
                  id="total"
                  inputMode="decimal"
                  {...register("total")}
                  placeholder="0,00"
                />
                {errors.total && (
                  <p className="text-sm text-destructive">
                    {errors.total.message}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="iva">
                {t("expenses.new.iva", "IVA")}
              </Label>
              <Input
                id="iva"
                inputMode="decimal"
                {...register("iva")}
                placeholder="0,00"
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  "expenses.new.ivaHelp",
                  "Si el ticket discrimina IVA, te ayuda con tu IVA compras.",
                )}
              </p>
              {errors.iva && (
                <p className="text-sm text-destructive">
                  {errors.iva.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("expenses.new.detail", "Categoría y notas")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t("expenses.new.category", "Categoría")}</Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? "Otros"} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">
                {t("expenses.new.notes", "Notas")}
              </Label>
              <Textarea
                id="notes"
                rows={3}
                {...register("notes")}
                placeholder={t(
                  "expenses.new.notesPlaceholder",
                  "Cliente, motivo, número de ticket…",
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-row justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/expenses")}
            >
              {t("expenses.new.cancel", "Cancelar")}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending
                ? t("expenses.new.saving", "Guardando…")
                : t("expenses.new.save", "Guardar")}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
